import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Tooltip,
  CircularProgress,
  Stack,
} from '@mui/material';

import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { StopManager } from '../../fixedRoutes/StopManager';
import { RouteBuilder } from '../../fixedRoutes/RouteBuilder';
import { TimetableTimeline } from '../../fixedRoutes/TimetableTimeline';
import { ExportImportControls } from '../../fixedRoutes/ExportImportControls';
import { FixedRoutesStatistics } from '../../fixedRoutes/FixedRoutesStatistics';
import { FrodoStatisticsTest } from '../../fixedRoutes/FrodoStatisticsTest';
import NetworkRoutesSection from '../../fixedRoutes/NetworkRoutesSection';
import { exportToExcel } from '../../../utils/fixedRoutes/exportHelpers';
import { loadGTFSDataFromFile, convertGTFSFeedToFixedRoutes, summarizeServiceWindows, type ServicePreference } from '../../../utils/fixedRoutes/gtfsConverter';
import { logEvent, trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import type { FixedRoutesTabName } from '../../../services/eventTypes';
import { liftangoBrandTokens } from '../../../theme';
import { opsColors } from '../../../theme/opsTokens';
import { consumePendingGtfsFile } from '../handlers/gtfsImportEvent';
import { isRouteEligibleForRostering } from '../../../features/rulesEngine/routeSnapshots';

import { Icon } from '@liftango/ui-icons';

// Tab index → analytics tab name. Order must match the <Tab> elements rendered below.
const TAB_NAMES: readonly FixedRoutesTabName[] = ['routes', 'stops', 'timetable', 'statistics', 'frodo', 'data', 'network'];

interface FixedRoutesContentProps {
  onClose: () => void;
  selectedRouteId?: string | null;
  isDrawerOpen?: boolean;
}

/**
 * Fixed Routes Content Component
 * 
 * Main content area for managing fixed routes (including circulators).
 * Wraps adapted CirculatorWebpage components in Material-UI styling.
 */
function FixedRoutesContentInner({ onClose: _onClose, selectedRouteId, isDrawerOpen = false }: FixedRoutesContentProps) {
  const { t } = useTranslation(['mapCore', 'common']);
  const navigate = useNavigate();
  const { routes, stops, trafficMultiplier, bulkAddStopsRoutes, setRosteringRouteIds } = useFixedRoutes();
  const shell = liftangoBrandTokens.fixedRoutes;
  const [selectedTab, setSelectedTab] = useState(0);
  const previousTabRef = useRef<FixedRoutesTabName | null>(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(new Set());
  const [gtfsLoading, setGtfsLoading] = useState(false);
  const [gtfsProgress, setGtfsProgress] = useState(0);
  const [gtfsError, setGtfsError] = useState<string | null>(null);
  const [isGtfsImportContext, setIsGtfsImportContext] = useState(false);
  const [gtfsPreferredService] = useState<ServicePreference>('weekday');
  
  // Switch to Routes tab if a route is selected
  React.useEffect(() => {
    if (selectedRouteId && !isGtfsImportContext) {
      setSelectedTab(0); // Switch to Routes tab
    }
  }, [selectedRouteId, isGtfsImportContext]);
  
  // Get selected route for timetable
  const selectedRoute = React.useMemo(() => {
    if (selectedRouteId) {
      return routes.find((r) => r.id === selectedRouteId) || null;
    }
    return null;
  }, [selectedRouteId, routes]);

  // Handle download dialog open
  const handleDownloadClick = () => {
    // Pre-select all routes by default
    setSelectedRouteIds(new Set(routes.map(r => r.id)));
    setDownloadDialogOpen(true);
  };

  const handleUseInRostering = useCallback(() => {
    const eligible = routes.filter(isRouteEligibleForRostering);
    if (!eligible.length) return;

    const preferred =
      selectedRouteId && eligible.some((r) => r.id === selectedRouteId)
        ? [selectedRouteId]
        : eligible.map((r) => r.id);

    setRosteringRouteIds(preferred);
    navigate('/rules-engine?tab=rostering');
  }, [routes, selectedRouteId, setRosteringRouteIds, navigate]);

  // Track user-initiated tab switches (not programmatic ones, e.g. GTFS import auto-switch).
  const handleTabChange = useCallback((newValue: number) => {
    const tabName = TAB_NAMES[newValue];
    if (tabName) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_routes_tab_changed, {
        tab_name: tabName,
        previous_tab: previousTabRef.current,
        timestamp: new Date().toISOString(),
      });
      previousTabRef.current = tabName;
    }
    setSelectedTab(newValue);
  }, []);

  // Handle checkbox toggle
  const handleToggleRoute = (routeId: string) => {
    setSelectedRouteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedRouteIds.size === routes.length) {
      setSelectedRouteIds(new Set());
    } else {
      setSelectedRouteIds(new Set(routes.map(r => r.id)));
    }
  };

  // Handle download
  const handleDownload = () => {
    const selectedRoutes = routes.filter(r => selectedRouteIds.has(r.id));
    if (selectedRoutes.length === 0) {
      return;
    }
    exportToExcel(selectedRoutes, stops, trafficMultiplier);
    setDownloadDialogOpen(false);
    setSelectedRouteIds(new Set());
  };

  // Process GTFS file (always uses merge mode for drag and drop)
  const processGTFSFile = useCallback(async (file: File) => {
    if (!file || !file.name.endsWith('.zip')) {
      setGtfsError(t('fixedRoutesContent.dropGtfsZip'));
      return;
    }

    setGtfsLoading(true);
    setIsGtfsImportContext(true);
    setGtfsProgress(0);
    setGtfsError(null);

    const startTime = Date.now();

    try {
      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportInitiated',
        label: file.name,
        file_size: file.size,
        file_size_mb: Math.round(file.size / 1024 / 1024 * 100) / 100,
        import_mode: 'merge',
      });

      setGtfsProgress(10);
      let gtfsData = await loadGTFSDataFromFile(file);

      setGtfsProgress(30);
      const summaries = summarizeServiceWindows(gtfsData.calendar, gtfsData.calendarDates);
      
      // Auto-select service
      let selectedServiceId = '';
      if (summaries.length === 1) {
        selectedServiceId = summaries[0].serviceId;
      } else if (summaries.length > 1) {
        const weekdayService = summaries.find(s => s.preference === 'weekday');
        selectedServiceId = weekdayService?.serviceId || summaries[0].serviceId;
      }

      setGtfsProgress(50);
      
      const isLargeImport = gtfsData.stops.length > 5000 || gtfsData.routes.length > 1000;
      const result = convertGTFSFeedToFixedRoutes(gtfsData, {
        preferredService: gtfsPreferredService,
        targetServiceId: selectedServiceId || undefined,
      });

      setGtfsProgress(70);
      
      bulkAddStopsRoutes(result.stops, result.routes, 'merge');

      // Clear large GTFS data from memory after processing
      // This helps with garbage collection, especially for large files
      if (isLargeImport) {
        // Force garbage collection opportunity
        await new Promise(resolve => setTimeout(resolve, 100));
        // Clear references to help GC
        (gtfsData as any) = null;
      }

      setGtfsProgress(100);

      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportSuccess',
        label: file.name,
        stops_created: result.stats.totalStops,
        routes_created: result.stats.totalRoutes,
        routes_skipped: result.stats.skippedRoutes,
        warnings_count: result.warnings.length,
        processing_time_ms: Date.now() - startTime,
        import_mode: 'merge',
      });

      setGtfsError(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('fixedRoutesContent.unknownError');
      setGtfsError(t('fixedRoutesContent.importFailed', { message: errorMessage }));

      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportError',
        label: file.name,
        error_message: errorMessage,
        processing_time_ms: Date.now() - startTime,
        import_mode: 'merge',
      });
    } finally {
      setGtfsLoading(false);
      setGtfsProgress(0);
      setIsGtfsImportContext(false);
    }
  }, [gtfsPreferredService, bulkAddStopsRoutes]);

  // Listen for global GTFS file drops from anywhere (map, drawer, etc.)
  React.useEffect(() => {
    const handleGlobalGTFSDrop = (event: CustomEvent<{ file: File }>) => {
      const { file } = event.detail;
      if (file && file.name.endsWith('.zip')) {
        setSelectedTab(5); // Data tab (avoid opening create-route mode during GTFS import)
        consumePendingGtfsFile();
        processGTFSFile(file);
      }
    };

    window.addEventListener('gtfsFileDropped', handleGlobalGTFSDrop as EventListener);
    return () => {
      window.removeEventListener('gtfsFileDropped', handleGlobalGTFSDrop as EventListener);
    };
  }, [processGTFSFile]);

  React.useEffect(() => {
    const pendingFile = consumePendingGtfsFile();
    if (pendingFile && pendingFile.name.endsWith('.zip')) {
      setSelectedTab(5); // Data tab (avoid opening create-route mode during GTFS import)
      processGTFSFile(pendingFile);
    }
  }, [processGTFSFile]);

  // Drag and drop handler for the drawer itself - always uses merge mode
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && acceptedFiles[0].name.endsWith('.zip')) {
      setSelectedTab(5); // Data tab
      await processGTFSFile(acceptedFiles[0]);
    } else {
      setGtfsError(t('fixedRoutesContent.dropGtfsZip'));
    }
  }, [processGTFSFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      console.log('FR drawer dropzone onDrop called with:', acceptedFiles);
      onDrop(acceptedFiles);
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    multiple: false,
    noClick: true, // Only allow drag and drop, not click
  });

  const primaryActionSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 1.5,
    backgroundColor: shell.primaryActionBackground,
    color: shell.primaryActionText,
    '&:hover': {
      backgroundColor: shell.primaryActionBackgroundHover,
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(58, 58, 72, 0.16)',
      color: 'rgba(58, 58, 72, 0.45)',
    },
  };

  const secondaryActionSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 1.5,
    backgroundColor: shell.secondaryActionBackground,
    color: shell.secondaryActionText,
    border: `1px solid ${shell.secondaryActionBorder}`,
    '&:hover': {
      backgroundColor: shell.tabBackground,
      borderColor: shell.tabBorder,
    },
  };

  return (
    <Box 
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} 
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      
      {/* Drag and Drop Overlay */}
      {isDragActive && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            background: 'rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              width: '100%',
              height: '100%',
            }}
          >
            <img 
              src="/icons/LiftangoLogo.png" 
              alt="Liftango Logo" 
              style={{ 
                maxWidth: '50vw', 
                maxHeight: '50vh', 
                width: '50%', 
                height: 'auto', 
                margin: '0 auto', 
                display: 'block', 
                filter: 'drop-shadow(0 2px 16px #000)' 
              }} 
            />
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mt: 4, textShadow: '0 2px 8px #000' }}>
              {t('fixedRoutesContent.dropToImport')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#fff', opacity: 0.85, mt: 1 }}>
              {t('fixedRoutesContent.dropSupported')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* GTFS Import Progress/Error */}
      {gtfsLoading && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'info.light',
            borderRadius: 1,
          }}
        >
          {/* Indeterminate so the wheel keeps spinning even while the (synchronous)
              GTFS conversion blocks the main thread — matches the simulation-results
              loading spinner. */}
          <CircularProgress size={24} sx={{ color: shell.tabActive }} />
          <Typography variant="body2">
            {t('fixedRoutesContent.processingProgress', { percent: Math.round(gtfsProgress) })}
          </Typography>
        </Box>
      )}
      {gtfsError && (
        <Box sx={{ p: 2, backgroundColor: 'error.light', mb: 2 }}>
          <Typography variant="body2" color="error">
            {gtfsError}
          </Typography>
        </Box>
      )}

      {/* Content + vertical tab rail */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, gap: 1 }}>
      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {selectedTab === 0 && (
          <Box sx={{ borderRadius: 2, backgroundColor: shell.contentSurface, overflow: 'hidden' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.5, pt: 1.5, pb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {routes.filter(isRouteEligibleForRostering).length} route
                {routes.filter(isRouteEligibleForRostering).length === 1 ? '' : 's'} with timings
              </Typography>
              <Tooltip
                title={
                  selectedRouteId
                    ? 'Send the selected route (or all timed routes) to Advance Rostering'
                    : 'Send all timed fixed routes to Advance Rostering'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!routes.some(isRouteEligibleForRostering)}
                    onClick={handleUseInRostering}
                    data-testid="fixed-routes.use-in-rostering"
                  >
                    Use in Rostering
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <RouteBuilder isDrawerOpen={isDrawerOpen} />
          </Box>
        )}

        {selectedTab === 1 && (
          <Box sx={{ borderRadius: 2, backgroundColor: shell.contentSurface, overflow: 'hidden' }}>
            <StopManager />
          </Box>
        )}

        {selectedTab === 2 && (
          <Box sx={{ borderRadius: 2, backgroundColor: shell.contentSurface, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('fixedRoutesContent.timetableHeading')}
              </Typography>
              <Tooltip title={t('fixedRoutesContent.downloadExcelTooltip')}>
                <Button
                  variant="contained"
                  startIcon={<Icon size={20} name="file-arrow-down" />}
                  onClick={handleDownloadClick}
                  disabled={routes.length === 0}
                  sx={primaryActionSx}
                >
                  {t('fixedRoutesContent.downloadExcel')}
                </Button>
              </Tooltip>
            </Box>
            <TimetableTimeline selectedRoute={selectedRoute} />
          </Box>
        )}

        {selectedTab === 3 && (
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: shell.contentSurfaceMuted }}>
            <FixedRoutesStatistics />
          </Box>
        )}

        {selectedTab === 4 && (
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: shell.contentSurfaceMuted }}>
            <FrodoStatisticsTest />
          </Box>
        )}

        {selectedTab === 5 && (
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: shell.contentSurfaceMuted }}>
            <ExportImportControls />
          </Box>
        )}

        {selectedTab === 6 && (
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: shell.contentSurface }}>
            <NetworkRoutesSection onRequestRoutesTab={() => setSelectedTab(0)} />
          </Box>
        )}
      </Box>

      {/* Vertical Tab Navigation */}
      <Tabs
        orientation="vertical"
        value={selectedTab}
        onChange={(_, newValue) => handleTabChange(newValue)}
        sx={{
          flexShrink: 0,
          mr: -1,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minWidth: 56,
            minHeight: 56,
            gap: 0.25,
            px: 0.5,
            // Fixed px (not theme units) so this matches the outer rail's 10px
            // exactly — theme.shape.borderRadius is 12, so `2.5` would be 30px.
            borderRadius: '10px',
            mb: 0.5,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
            fontSize: '0.5625rem',
            color: 'text.primary',
            border: '1px solid transparent',
          },
          // Icons match the outer app rail: always text.primary, size 20.
          '& .MuiTab-root .MuiTab-iconWrapper': {
            color: 'text.primary',
          },
          '& .MuiTab-root.Mui-selected': {
            color: 'text.primary',
            backgroundColor: opsColors.neutral.panelAlt,
            borderColor: opsColors.neutral.lineStrong,
          },
        }}
      >
        <Tab icon={<Icon name="route" size={20} />} label={t('fixedRoutesContent.tabs.routes')} />
        <Tab icon={<Icon name="location-dot" size={20} />} label={t('fixedRoutesContent.tabs.stops')} />
        <Tab icon={<Icon name="clock" size={20} />} label={t('fixedRoutesContent.tabs.timetable')} />
        <Tab icon={<Icon name="chart-bar" size={20} />} label={t('fixedRoutesContent.tabs.statistics')} />
        <Tab icon={<Icon name="rocket" size={20} />} label={t('fixedRoutesContent.tabs.frodo')} />
        <Tab icon={<Icon name="file-import" size={20} />} label={t('fixedRoutesContent.tabs.data')} />
        <Tab icon={<Icon name="map" size={20} />} label={t('fixedRoutesContent.tabs.network')} />
      </Tabs>
      </Box>

      {/* Download Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 2500 }}
      >
        <DialogTitle>
          {t('fixedRoutesContent.selectRoutesToDownload')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              onClick={handleSelectAll}
              size="small"
              sx={secondaryActionSx}
            >
              {selectedRouteIds.size === routes.length ? t('fixedRoutesContent.deselectAll') : t('fixedRoutesContent.selectAll')}
            </Button>
          </Box>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {routes.map((route) => (
              <ListItem key={route.id} disablePadding>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRouteIds.has(route.id)}
                      onChange={() => handleToggleRoute(route.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {route.name}
                      </Typography>
                      {route.shortName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                          {route.shortName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t('fixedRoutesContent.stopsCount', { count: route.stops.length })} • {route.startTime}
                        {route.endTime && ` - ${route.endTime}`}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </ListItem>
            ))}
            {routes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'left' }}>
                {t('fixedRoutesContent.noRoutesAvailable')}
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)} sx={secondaryActionSx}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            disabled={selectedRouteIds.size === 0}
            sx={primaryActionSx}
          >
            {t('fixedRoutesContent.downloadCount', { count: selectedRouteIds.size })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export const FixedRoutesContent: React.FC<FixedRoutesContentProps> = ({ onClose, selectedRouteId, isDrawerOpen = false }) => {
  // FixedRoutesProvider is now at App level, so we don't need to wrap here
  return <FixedRoutesContentInner onClose={onClose} selectedRouteId={selectedRouteId} isDrawerOpen={isDrawerOpen} />;
};

export default FixedRoutesContent;
